import { Injectable, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { LoggingService } from '@fleet-monitor/frontend-logging';
import { TermsAcceptanceApiService } from '../../../services/api/terms-acceptance-api.service';
import { TermsStatus } from '../../../models/terms-acceptance';
import {
  remindedLaterFor,
  rememberRemindLater,
} from '../../../services/state/terms-reminder';

export type TermsPromptState = 'hidden' | 'modal' | 'banner';

/**
 * Soft gate for the Terms and Conditions: asks a User who hasn't accepted the
 * current Agreement version to accept it, without ever blocking the app.
 */
@Injectable({
  providedIn: 'root',
})
export class TermsPromptService {
  private api = inject(TermsAcceptanceApiService);
  private logger = inject(LoggingService);

  readonly state = signal<TermsPromptState>('hidden');
  readonly agreement = signal<TermsStatus | undefined>(undefined);
  readonly error = signal<string | undefined>(undefined);

  check(): void {
    this.api.status().subscribe({
      next: (status) => {
        this.agreement.set(status);
        if (status.accepted) this.state.set('hidden');
        else
          this.state.set(
            remindedLaterFor() === status.version ? 'banner' : 'modal'
          );
      },
      // Fail open: never stand between a User and the app over the Terms.
      error: (error) =>
        this.logger.warn(
          '[TermsPrompt] Could not load the Terms status',
          error
        ),
    });
  }

  remindLater(): void {
    const agreement = this.agreement();
    if (agreement) rememberRemindLater(agreement.version);
    this.state.set('banner');
  }

  review(): void {
    this.state.set('modal');
  }

  agree(): void {
    const agreement = this.agreement();
    if (!agreement) return;
    this.error.set(undefined);
    this.api.accept(agreement.version).subscribe({
      next: () => this.state.set('hidden'),
      error: (error: HttpErrorResponse) => {
        // A newer version took effect while the User was deciding: show it.
        if (error.status === 400) return this.check();
        this.logger.warn(
          '[TermsPrompt] Could not record the acceptance',
          error
        );
        this.error.set(
          'We could not record your acceptance. Please try again.'
        );
      },
    });
  }
}
